// app/src/App.jsx — the studio shell (D2a, pixel-close to the handoff): a left rail with THREE
// destinations (Create / My Content / Brands) on desktop that becomes a bottom tab bar on
// mobile, the account+credits in the rail footer, the out-of-credits banner, and the create
// spine as routes. The ROUTES + data flow are the D1 contract; D2a changes only the LOOK.

import React from 'react';
import { Sparkles, LayoutGrid, Bookmark } from 'lucide-react';
import { Router, Link, navigate, useRoute } from '@common/router.js';
import { useI18n } from './i18n/index.jsx';
import { useSession } from './lib/session.jsx';
import { useTopUp } from './lib/topup.jsx';
import SignIn from './screens/SignIn.jsx';
import SignUp from './screens/SignUp.jsx';
import AuthAction from './screens/AuthAction.jsx';
import VerifyEmail from './screens/VerifyEmail.jsx';
import OAuthComplete from './screens/OAuthComplete.jsx';
import CreateHome from './screens/CreateHome.jsx';
import Ideas from './screens/Ideas.jsx';
import Script from './screens/Script.jsx';
import Generating from './screens/Generating.jsx';
import Result from './screens/Result.jsx';
import MyContent from './screens/MyContent.jsx';
import Brands from './screens/Brands.jsx';
import CreditsReturn from './screens/CreditsReturn.jsx';
import Maintenance from './screens/Maintenance.jsx';
import AccountFoot from './components/AccountFoot.jsx';
import LocaleSync from './lib/locale-sync.jsx';

// the auth flow pages ride BOTH route trees (D-AUTH): signing in mid-flow flips `authed`
// and swaps the tree — the page must stay mounted at its path or the flow dies.
const AUTH_FLOW_ROUTES = [
  { path: '/auth/action', render: () => <AuthAction /> },
  { path: '/auth/google-complete', render: () => <OAuthComplete provider="google" /> },
  { path: '/auth/linkedin-complete', render: () => <OAuthComplete provider="linkedin" /> },
];

const ROUTES = [
  { path: '/', render: () => <CreateHome /> },
  { path: '/create/ideas', render: () => <Ideas /> },
  { path: '/create/script', render: () => <Script /> },
  { path: '/create/generating/:jobId', render: (p) => <Generating jobId={p.jobId} /> },
  { path: '/result/:runId', render: (p) => <Result runId={p.runId} /> },
  { path: '/content', render: () => <MyContent /> },
  { path: '/brands', render: () => <Brands /> },
  { path: '/credits/return', render: () => <CreditsReturn /> },
  ...AUTH_FLOW_ROUTES,
];

// signed-out: a real router (signin/signup/verify/oauth-complete), not one hardwired frame
const SIGNED_OUT_ROUTES = [
  { path: '/signin', render: () => <SignIn /> },
  { path: '/signup', render: () => <SignUp /> },
  ...AUTH_FLOW_ROUTES,
];

// the three destinations — icon keeps its brand colour in the rail; goes coral when active on the
// mobile tab bar (matches both comps).
const NAV = [
  { key: 'create', to: '/', Icon: Sparkles, color: 'var(--coral)', tKey: 'nav.create', tabKey: 'nav.create', match: (p) => p === '/' || p.startsWith('/create') || p.startsWith('/result') },
  { key: 'content', to: '/content', Icon: LayoutGrid, color: 'var(--violet)', tKey: 'nav.content', tabKey: 'nav.contentTab', match: (p) => p.startsWith('/content') },
  { key: 'brands', to: '/brands', Icon: Bookmark, color: 'var(--mint)', tKey: 'nav.brands', tabKey: 'nav.brands', match: (p) => p.startsWith('/brands') },
];

function Rail() {
  const { t } = useI18n();
  const { pathname } = useRoute();
  return (
    <aside className="rail">
      <div className="rail-brand">
        <img src="/logo-mark.png" alt="" />
        <span className="word">{t('app.short')}</span>
        <span className="beta">{t('app.beta')}</span>
      </div>
      <div className="rail-nav-group">
        {NAV.map(({ key, to, Icon, color, tKey, match }) => {
          const on = match(pathname);
          return (
            <Link key={key} to={to} className={`rail-nav ${key}${on ? ' active' : ''}`}>
              <span className="inner"><Icon size={18} color={color} strokeWidth={2} />{t(tKey)}</span>
            </Link>
          );
        })}
      </div>
      <div className="rail-spacer" />
      <AccountFoot />
    </aside>
  );
}

function TabBar() {
  const { t } = useI18n();
  const { pathname } = useRoute();
  return (
    <nav className="tabbar">
      {NAV.map(({ key, to, Icon, tabKey, match }) => {
        const on = match(pathname);
        return (
          <button key={key} className={`tab${on ? ' on' : ''}`} onClick={() => navigate(to)}>
            <Icon size={23} color={on ? 'var(--coral)' : 'var(--step-off)'} strokeWidth={2} />
            <span className="lbl">{t(tabKey)}</span>
          </button>
        );
      })}
    </nav>
  );
}

function OutOfCreditsBanner() {
  const { t } = useI18n();
  const { credits } = useSession();
  const openTopUp = useTopUp();
  if (!credits || credits.balance > 0) return null; // fires on <= 0 — a negative balance is still "out" (§5.3)
  return (
    <div className="ooc-banner">
      <span className="pill">{t('credits.zeroLeft')}</span>
      <span style={{ fontWeight: 500, fontSize: 13 }}>{t('credits.oocBanner')}</span>
      <button className="q-btn press" onClick={openTopUp} style={{ marginInlineStart: 'auto', background: 'var(--coral)', color: '#fff', borderRadius: 'var(--r-pill)', padding: '8px 16px', fontWeight: 700, fontSize: 12.5 }}>{t('credits.topUpShort')}</button>
    </div>
  );
}

// The HARD email-verify gate (Daniel's call, 2026-07-06 — SUPERSEDES the original D-AUTH §4
// "browsing is NOT gated" recommendation; see D-AUTH-DESIGN.md §4): a signed-in but
// email-unverified PASSWORD account can't reach the studio — it's held on the Verify-Email
// page until it confirms. OAuth users have no password provider ⇒ trusted as provider-verified
// ⇒ never gated. Only the real email-action + oauth-completion routes render bare while gated
// (they are HOW a user verifies/signs in) — matched by EXACT path, never a `/auth/` prefix (an
// unmatched `/auth/*` would fall to the fallback view and slip past the gate). Dev-rig
// principals are stamped verified, so the walkthrough is unaffected.
function SignedInShell({ view }) {
  const { t } = useI18n();
  const { pathname } = useRoute();
  const { me, auth } = useSession();
  if (AUTH_FLOW_ROUTES.some((r) => r.path === pathname)) return view; // verify + oauth-complete render bare
  const real = auth.mode !== 'dev';
  if (real && me === null) return <div className="app-pad muted">{t('common.loading')}</div>;
  if (real && me && me.email_verified === false && auth.hasPasswordProvider()) return <VerifyEmail />;
  return (
    <div className="app">
      <Rail />
      <main className="main">
        <OutOfCreditsBanner />
        <div className="app-pad"><div className="content-max">{view}</div></div>
      </main>
      <TabBar />
    </div>
  );
}

export default function App() {
  const { t } = useI18n();
  const { authed, maintenance } = useSession();
  // MAINT-MODE §8: a dark launch replaces the ENTIRE app (sign-in included) with the maintenance
  // page. `maintenance.on` already encodes the admin exemption (an admin's calls never 503, so
  // the session never marks them blocked) — App just renders the decision.
  if (maintenance && maintenance.on) return <Maintenance message={maintenance.message} />;
  if (authed === null) return <div className="app-pad muted">{t('common.loading')}</div>;
  if (!authed) {
    return (
      <>
        <LocaleSync />
        <Router routes={SIGNED_OUT_ROUTES} fallback={<SignIn />}>{(view) => view}</Router>
      </>
    );
  }
  return (
    <>
      <LocaleSync />
      <Router routes={ROUTES} fallback={<CreateHome />}>
        {(view) => <SignedInShell view={view} />}
      </Router>
    </>
  );
}
